"use client";

// packages/react/dialog/src/Dialog.tsx
import _defineProperty from "/vercel/path0/node_modules/@babel/runtime/helpers/esm/defineProperty.js";
import _objectWithoutProperties from "/vercel/path0/node_modules/@babel/runtime/helpers/esm/objectWithoutProperties.js";
import _slicedToArray from "/vercel/path0/node_modules/@babel/runtime/helpers/esm/slicedToArray.js";
var _excluded = ["__scopeDialog"],
  _excluded2 = ["forceMount"],
  _excluded3 = ["__scopeDialog"],
  _excluded4 = ["forceMount"],
  _excluded5 = ["__scopeDialog", "trapFocus", "onOpenAutoFocus", "onCloseAutoFocus"],
  _excluded6 = ["__scopeDialog"],
  _excluded7 = ["__scopeDialog"],
  _excluded8 = ["__scopeDialog"];
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
import * as React from "react";
import { composeEventHandlers } from "@radix-ui/primitive";
import { useComposedRefs } from "@radix-ui/react-compose-refs";
import { createContext, createContextScope } from "@radix-ui/react-context";
import { useId } from "@radix-ui/react-id";
import { useControllableState } from "@radix-ui/react-use-controllable-state";
import { DismissableLayer } from "@radix-ui/react-dismissable-layer";
import { FocusScope } from "@radix-ui/react-focus-scope";
import { Portal as PortalPrimitive } from "@radix-ui/react-portal";
import { Presence } from "@radix-ui/react-presence";
import { Primitive } from "@radix-ui/react-primitive";
import { useFocusGuards } from "@radix-ui/react-focus-guards";
import { RemoveScroll } from "react-remove-scroll";
import { hideOthers } from "aria-hidden";
import { Slot } from "@radix-ui/react-slot";
import { Fragment, jsx, jsxs } from "react/jsx-runtime";
var DIALOG_NAME = "Dialog";
var _createContextScope = createContextScope(DIALOG_NAME),
  _createContextScope2 = _slicedToArray(_createContextScope, 2),
  createDialogContext = _createContextScope2[0],
  createDialogScope = _createContextScope2[1];
var _createDialogContext = createDialogContext(DIALOG_NAME),
  _createDialogContext2 = _slicedToArray(_createDialogContext, 2),
  DialogProvider = _createDialogContext2[0],
  useDialogContext = _createDialogContext2[1];
var Dialog = function Dialog(props) {
  var __scopeDialog = props.__scopeDialog,
    children = props.children,
    openProp = props.open,
    defaultOpen = props.defaultOpen,
    onOpenChange = props.onOpenChange,
    _props$modal = props.modal,
    modal = _props$modal === void 0 ? true : _props$modal;
  var triggerRef = React.useRef(null);
  var contentRef = React.useRef(null);
  var _useControllableState = useControllableState({
      prop: openProp,
      defaultProp: defaultOpen,
      onChange: onOpenChange
    }),
    _useControllableState2 = _slicedToArray(_useControllableState, 2),
    _useControllableState3 = _useControllableState2[0],
    open = _useControllableState3 === void 0 ? false : _useControllableState3,
    setOpen = _useControllableState2[1];
  return /* @__PURE__ */jsx(DialogProvider, {
    scope: __scopeDialog,
    triggerRef: triggerRef,
    contentRef: contentRef,
    contentId: useId(),
    titleId: useId(),
    descriptionId: useId(),
    open: open,
    onOpenChange: setOpen,
    onOpenToggle: React.useCallback(function () {
      return setOpen(function (prevOpen) {
        return !prevOpen;
      });
    }, [setOpen]),
    modal: modal,
    children: children
  });
};
Dialog.displayName = DIALOG_NAME;
var TRIGGER_NAME = "DialogTrigger";
var DialogTrigger = React.forwardRef(function (props, forwardedRef) {
  var __scopeDialog = props.__scopeDialog,
    triggerProps = _objectWithoutProperties(props, _excluded);
  var context = useDialogContext(TRIGGER_NAME, __scopeDialog);
  var composedTriggerRef = useComposedRefs(forwardedRef, context.triggerRef);
  return /* @__PURE__ */jsx(Primitive.button, _objectSpread(_objectSpread({
    type: "button",
    "aria-haspopup": "dialog",
    "aria-expanded": context.open,
    "aria-controls": context.contentId,
    "data-state": getState(context.open)
  }, triggerProps), {}, {
    ref: composedTriggerRef,
    onClick: composeEventHandlers(props.onClick, context.onOpenToggle)
  }));
});
DialogTrigger.displayName = TRIGGER_NAME;
var PORTAL_NAME = "DialogPortal";
var _createDialogContext3 = createDialogContext(PORTAL_NAME, {
    forceMount: void 0
  }),
  _createDialogContext4 = _slicedToArray(_createDialogContext3, 2),
  PortalProvider = _createDialogContext4[0],
  usePortalContext = _createDialogContext4[1];
var DialogPortal = function DialogPortal(props) {
  var __scopeDialog = props.__scopeDialog,
    forceMount = props.forceMount,
    children = props.children,
    container = props.container;
  var context = useDialogContext(PORTAL_NAME, __scopeDialog);
  return /* @__PURE__ */jsx(PortalProvider, {
    scope: __scopeDialog,
    forceMount: forceMount,
    children: React.Children.map(children, function (child) {
      return /* @__PURE__ */jsx(Presence, {
        present: forceMount || context.open,
        children: /* @__PURE__ */jsx(PortalPrimitive, {
          asChild: true,
          container: container,
          children: child
        })
      });
    })
  });
};
DialogPortal.displayName = PORTAL_NAME;
var OVERLAY_NAME = "DialogOverlay";
var DialogOverlay = React.forwardRef(function (props, forwardedRef) {
  var portalContext = usePortalContext(OVERLAY_NAME, props.__scopeDialog);
  var _props$forceMount = props.forceMount,
    forceMount = _props$forceMount === void 0 ? portalContext.forceMount : _props$forceMount,
    overlayProps = _objectWithoutProperties(props, _excluded2);
  var context = useDialogContext(OVERLAY_NAME, props.__scopeDialog);
  return context.modal ? /* @__PURE__ */jsx(Presence, {
    present: forceMount || context.open,
    children: /* @__PURE__ */jsx(DialogOverlayImpl, _objectSpread(_objectSpread({}, overlayProps), {}, {
      ref: forwardedRef
    }))
  }) : null;
});
DialogOverlay.displayName = OVERLAY_NAME;
var DialogOverlayImpl = React.forwardRef(function (props, forwardedRef) {
  var __scopeDialog = props.__scopeDialog,
    overlayProps = _objectWithoutProperties(props, _excluded3);
  var context = useDialogContext(OVERLAY_NAME, __scopeDialog);
  return (
    // Make sure `Content` is scrollable even when it doesn't live inside `RemoveScroll`
    // ie. when `Overlay` and `Content` are siblings
    /* @__PURE__ */
    jsx(RemoveScroll, {
      as: Slot,
      allowPinchZoom: true,
      shards: [context.contentRef],
      children: /* @__PURE__ */jsx(Primitive.div, _objectSpread(_objectSpread({
        "data-state": getState(context.open)
      }, overlayProps), {}, {
        ref: forwardedRef,
        style: _objectSpread({
          pointerEvents: "auto"
        }, overlayProps.style)
      }))
    })
  );
});
var CONTENT_NAME = "DialogContent";
var DialogContent = React.forwardRef(function (props, forwardedRef) {
  var portalContext = usePortalContext(CONTENT_NAME, props.__scopeDialog);
  var _props$forceMount2 = props.forceMount,
    forceMount = _props$forceMount2 === void 0 ? portalContext.forceMount : _props$forceMount2,
    contentProps = _objectWithoutProperties(props, _excluded4);
  var context = useDialogContext(CONTENT_NAME, props.__scopeDialog);
  return /* @__PURE__ */jsx(Presence, {
    present: forceMount || context.open,
    children: context.modal ? /* @__PURE__ */jsx(DialogContentModal, _objectSpread(_objectSpread({}, contentProps), {}, {
      ref: forwardedRef
    })) : /* @__PURE__ */jsx(DialogContentNonModal, _objectSpread(_objectSpread({}, contentProps), {}, {
      ref: forwardedRef
    }))
  });
});
DialogContent.displayName = CONTENT_NAME;
var DialogContentModal = React.forwardRef(function (props, forwardedRef) {
  var context = useDialogContext(CONTENT_NAME, props.__scopeDialog);
  var contentRef = React.useRef(null);
  var composedRefs = useComposedRefs(forwardedRef, context.contentRef, contentRef);
  React.useEffect(function () {
    var content = contentRef.current;
    if (content) return hideOthers(content);
  }, []);
  return /* @__PURE__ */jsx(DialogContentImpl, _objectSpread(_objectSpread({}, props), {}, {
    ref: composedRefs,
    trapFocus: context.open,
    disableOutsidePointerEvents: true,
    onCloseAutoFocus: composeEventHandlers(props.onCloseAutoFocus, function (event) {
      var _context$triggerRef$c;
      event.preventDefault();
      (_context$triggerRef$c = context.triggerRef.current) === null || _context$triggerRef$c === void 0 ? void 0 : _context$triggerRef$c.focus();
    }),
    onPointerDownOutside: composeEventHandlers(props.onPointerDownOutside, function (event) {
      var originalEvent = event.detail.originalEvent;
      var ctrlLeftClick = originalEvent.button === 0 && originalEvent.ctrlKey === true;
      var isRightClick = originalEvent.button === 2 || ctrlLeftClick;
      if (isRightClick) event.preventDefault();
    }),
    onFocusOutside: composeEventHandlers(props.onFocusOutside, function (event) {
      return event.preventDefault();
    })
  }));
});
var DialogContentNonModal = React.forwardRef(function (props, forwardedRef) {
  var context = useDialogContext(CONTENT_NAME, props.__scopeDialog);
  var hasInteractedOutsideRef = React.useRef(false);
  var hasPointerDownOutsideRef = React.useRef(false);
  return /* @__PURE__ */jsx(DialogContentImpl, _objectSpread(_objectSpread({}, props), {}, {
    ref: forwardedRef,
    trapFocus: false,
    disableOutsidePointerEvents: false,
    onCloseAutoFocus: function onCloseAutoFocus(event) {
      var _props$onCloseAutoFoc;
      (_props$onCloseAutoFoc = props.onCloseAutoFocus) === null || _props$onCloseAutoFoc === void 0 ? void 0 : _props$onCloseAutoFoc.call(props, event);
      if (!event.defaultPrevented) {
        var _context$triggerRef$c2;
        if (!hasInteractedOutsideRef.current) (_context$triggerRef$c2 = context.triggerRef.current) === null || _context$triggerRef$c2 === void 0 ? void 0 : _context$triggerRef$c2.focus();
        event.preventDefault();
      }
      hasInteractedOutsideRef.current = false;
      hasPointerDownOutsideRef.current = false;
    },
    onInteractOutside: function onInteractOutside(event) {
      var _props$onInteractOuts, _context$triggerRef$c3;
      (_props$onInteractOuts = props.onInteractOutside) === null || _props$onInteractOuts === void 0 ? void 0 : _props$onInteractOuts.call(props, event);
      if (!event.defaultPrevented) {
        hasInteractedOutsideRef.current = true;
        if (event.detail.originalEvent.type === "pointerdown") {
          hasPointerDownOutsideRef.current = true;
        }
      }
      var target = event.target;
      var targetIsTrigger = (_context$triggerRef$c3 = context.triggerRef.current) === null || _context$triggerRef$c3 === void 0 ? void 0 : _context$triggerRef$c3.contains(target);
      if (targetIsTrigger) event.preventDefault();
      if (event.detail.originalEvent.type === "focusin" && hasPointerDownOutsideRef.current) {
        event.preventDefault();
      }
    }
  }));
});
var DialogContentImpl = React.forwardRef(function (props, forwardedRef) {
  var __scopeDialog = props.__scopeDialog,
    trapFocus = props.trapFocus,
    onOpenAutoFocus = props.onOpenAutoFocus,
    onCloseAutoFocus = props.onCloseAutoFocus,
    contentProps = _objectWithoutProperties(props, _excluded5);
  var context = useDialogContext(CONTENT_NAME, __scopeDialog);
  var contentRef = React.useRef(null);
  var composedRefs = useComposedRefs(forwardedRef, contentRef);
  useFocusGuards();
  return /* @__PURE__ */jsxs(Fragment, {
    children: [/* @__PURE__ */jsx(FocusScope, {
      asChild: true,
      loop: true,
      trapped: trapFocus,
      onMountAutoFocus: onOpenAutoFocus,
      onUnmountAutoFocus: onCloseAutoFocus,
      children: /* @__PURE__ */jsx(DismissableLayer, _objectSpread(_objectSpread({
        role: "dialog",
        id: context.contentId,
        "aria-describedby": context.descriptionId,
        "aria-labelledby": context.titleId,
        "data-state": getState(context.open)
      }, contentProps), {}, {
        ref: composedRefs,
        onDismiss: function onDismiss() {
          return context.onOpenChange(false);
        }
      }))
    }), /* @__PURE__ */jsxs(Fragment, {
      children: [/* @__PURE__ */jsx(TitleWarning, {
        titleId: context.titleId
      }), /* @__PURE__ */jsx(DescriptionWarning, {
        contentRef: contentRef,
        descriptionId: context.descriptionId
      })]
    })]
  });
});
var TITLE_NAME = "DialogTitle";
var DialogTitle = React.forwardRef(function (props, forwardedRef) {
  var __scopeDialog = props.__scopeDialog,
    titleProps = _objectWithoutProperties(props, _excluded6);
  var context = useDialogContext(TITLE_NAME, __scopeDialog);
  return /* @__PURE__ */jsx(Primitive.h2, _objectSpread(_objectSpread({
    id: context.titleId
  }, titleProps), {}, {
    ref: forwardedRef
  }));
});
DialogTitle.displayName = TITLE_NAME;
var DESCRIPTION_NAME = "DialogDescription";
var DialogDescription = React.forwardRef(function (props, forwardedRef) {
  var __scopeDialog = props.__scopeDialog,
    descriptionProps = _objectWithoutProperties(props, _excluded7);
  var context = useDialogContext(DESCRIPTION_NAME, __scopeDialog);
  return /* @__PURE__ */jsx(Primitive.p, _objectSpread(_objectSpread({
    id: context.descriptionId
  }, descriptionProps), {}, {
    ref: forwardedRef
  }));
});
DialogDescription.displayName = DESCRIPTION_NAME;
var CLOSE_NAME = "DialogClose";
var DialogClose = React.forwardRef(function (props, forwardedRef) {
  var __scopeDialog = props.__scopeDialog,
    closeProps = _objectWithoutProperties(props, _excluded8);
  var context = useDialogContext(CLOSE_NAME, __scopeDialog);
  return /* @__PURE__ */jsx(Primitive.button, _objectSpread(_objectSpread({
    type: "button"
  }, closeProps), {}, {
    ref: forwardedRef,
    onClick: composeEventHandlers(props.onClick, function () {
      return context.onOpenChange(false);
    })
  }));
});
DialogClose.displayName = CLOSE_NAME;
function getState(open) {
  return open ? "open" : "closed";
}
var TITLE_WARNING_NAME = "DialogTitleWarning";
var _createContext = createContext(TITLE_WARNING_NAME, {
    contentName: CONTENT_NAME,
    titleName: TITLE_NAME,
    docsSlug: "dialog"
  }),
  _createContext2 = _slicedToArray(_createContext, 2),
  WarningProvider = _createContext2[0],
  useWarningContext = _createContext2[1];
var TitleWarning = function TitleWarning(_ref) {
  var titleId = _ref.titleId;
  var titleWarningContext = useWarningContext(TITLE_WARNING_NAME);
  var MESSAGE = "`".concat(titleWarningContext.contentName, "` requires a `").concat(titleWarningContext.titleName, "` for the component to be accessible for screen reader users.\n\nIf you want to hide the `").concat(titleWarningContext.titleName, "`, you can wrap it with our VisuallyHidden component.\n\nFor more information, see https://radix-ui.com/primitives/docs/components/").concat(titleWarningContext.docsSlug);
  React.useEffect(function () {
    if (titleId) {
      var hasTitle = document.getElementById(titleId);
      if (!hasTitle) console.error(MESSAGE);
    }
  }, [MESSAGE, titleId]);
  return null;
};
var DESCRIPTION_WARNING_NAME = "DialogDescriptionWarning";
var DescriptionWarning = function DescriptionWarning(_ref2) {
  var contentRef = _ref2.contentRef,
    descriptionId = _ref2.descriptionId;
  var descriptionWarningContext = useWarningContext(DESCRIPTION_WARNING_NAME);
  var MESSAGE = "Warning: Missing `Description` or `aria-describedby={undefined}` for {".concat(descriptionWarningContext.contentName, "}.");
  React.useEffect(function () {
    var _contentRef$current;
    var describedById = (_contentRef$current = contentRef.current) === null || _contentRef$current === void 0 ? void 0 : _contentRef$current.getAttribute("aria-describedby");
    if (descriptionId && describedById) {
      var hasDescription = document.getElementById(descriptionId);
      if (!hasDescription) console.warn(MESSAGE);
    }
  }, [MESSAGE, contentRef, descriptionId]);
  return null;
};
var Root = Dialog;
var Trigger = DialogTrigger;
var Portal = DialogPortal;
var Overlay = DialogOverlay;
var Content = DialogContent;
var Title = DialogTitle;
var Description = DialogDescription;
var Close = DialogClose;
export { Close, Content, Description, Dialog, DialogClose, DialogContent, DialogDescription, DialogOverlay, DialogPortal, DialogTitle, DialogTrigger, Overlay, Portal, Root, Title, Trigger, WarningProvider, createDialogScope };